import { createContext, useContext, useLayoutEffect, useRef, } from "react";
export const ConversationContext = createContext(null);
/**
 * Returns the raw `Conversation` instance (or `null` if no session is active).
 * This is a public escape hatch for advanced use cases that need direct access
 * to the underlying `@elevenlabs/client` Conversation object.
 *
 * Can be used outside a `ConversationProvider` — returns `null` in that case.
 */
export function useRawConversation() {
    const ctx = useContext(ConversationContext);
    return ctx?.conversation ?? null;
}
/**
 * Returns a stable ref to the active `Conversation` instance.
 * The ref's `.current` is `null` when no session is active, and updates
 * without causing re-renders — ideal for use inside callbacks and sub-providers.
 *
 * Must be used within a `ConversationProvider`.
 */
export function useRawConversationRef() {
    const ctx = useContext(ConversationContext);
    if (!ctx) {
        throw new Error("useRawConversationRef must be used within a ConversationProvider");
    }
    return ctx.conversationRef;
}
/**
 * Registers callback handlers with the nearest `ConversationProvider`.
 * Uses a ref internally so the latest callback values are always invoked
 * without re-subscribing on every render.
 *
 * Must be used within a `ConversationProvider`.
 */
export function useRegisterCallbacks(callbacks) {
    const ctx = useContext(ConversationContext);
    if (!ctx) {
        throw new Error("useRegisterCallbacks must be used within a ConversationProvider");
    }
    const { registerCallbacks } = ctx;
    const callbacksRef = useRef(callbacks);
    // Re-subscribe when the set of provided callback keys changes.
    const activeKeyToken = Object.keys(callbacks)
        .filter(key => callbacks[key] !== undefined)
        .sort()
        .join("|");
    useLayoutEffect(() => {
        callbacksRef.current = callbacks;
    });
    useLayoutEffect(() => {
        const activeKeys = activeKeyToken === "" ? [] : activeKeyToken.split("|");
        const stableCallbacks = Object.fromEntries(activeKeys.map((key) => [
            key,
            (...args) => {
                const fn = callbacksRef.current[key];
                if (typeof fn === "function") {
                    fn(...args);
                }
            },
        ]));
        return registerCallbacks(stableCallbacks);
    }, [registerCallbacks, activeKeyToken]);
}
//# sourceMappingURL=ConversationContext.js.map